import type { Vehicle, Rental, PaymentDue } from "@fleetlease/shared";
import { useList, money } from "./data";
import { Badge } from "./Badge";
import { StatsSkeleton, CardSkeleton } from "./ui";

export function Dashboard() {
  const vehicles = useList<Vehicle>("vehicles", "/vehicles");
  const rentals = useList<Rental>("rentals", "/rentals");
  const dues = useList<PaymentDue>("payments", "/payments");
  const loading = vehicles.isPending || rentals.isPending || dues.isPending;

  const v = (vehicles.data?.items ?? []).filter((x) => (x as any).type === "Vehicle");
  const r = rentals.data?.items ?? [];
  const d = dues.data?.items ?? [];
  const byStatus = (s: string) => v.filter((x) => x.status === s).length;
  const activeRentals = r.filter((x) => x.status === "ACTIVE" || x.status === "ENDING_SOON").length;
  const owed = d.reduce((sum, x) => sum + (x.amountCents ?? 0), 0);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-extrabold tracking-tight">Good morning</h1>

      {loading ? (
        <>
          <StatsSkeleton count={3} />
          <CardSkeleton lines={4} />
        </>
      ) : (
        <>
          <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card title="Open dues" value={money(owed)} sub={`${d.length} due${d.length === 1 ? "" : "s"} outstanding`} tone={d.length ? "warn" : "info"} />
            <Card title="Active rentals" value={String(activeRentals)} sub={`${r.length} total on record`} tone="info" />
            <Card title="Fleet" value={String(v.length)} sub={`${byStatus("RENTED")} rented · ${byStatus("AVAILABLE")} available · ${byStatus("IN_SHOP")} in shop`} tone="info" />
          </section>

          <section className="bg-paper-card rounded-2xl border border-black/[0.07] shadow-card p-5">
            <h2 className="text-sm font-semibold text-ink-muted mb-3">Open dues</h2>
            <ul className="divide-y divide-black/5">
              {d.length === 0 && <li className="py-2 text-ink-muted text-sm">Nothing outstanding.</li>}
              {d.map((x) => (
                <li key={x.id} className="py-2 flex items-center justify-between text-sm">
                  <span>Due {x.dueDate} · {money(x.amountCents)}</span>
                  <Badge tone={x.status === "OVERDUE" ? "danger" : x.status === "PARTIAL" ? "warn" : "info"}>{x.status}</Badge>
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
    </div>
  );
}

function Card({ title, value, sub, tone }: { title: string; value: string; sub: string; tone: string }) {
  return (
    <div className="bg-paper-card rounded-2xl border border-black/[0.07] shadow-card p-5">
      <div className="text-sm text-ink-muted">{title}</div>
      <div className="mt-1 text-3xl font-extrabold tracking-tight">{value}</div>
      <div className="mt-1 text-sm"><Badge tone={tone}>{sub}</Badge></div>
    </div>
  );
}
